import React, { useState, useEffect, forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { es } from 'date-fns/locale';
import { getMonth, getYear } from "date-fns";
import PropTypes from 'prop-types';
import Icon from '../icon/Icon';
import Input from '../input/Input';
import type { DatePickerProps } from './DatePicker.types';
import './datePicker.scss';

const months = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const DatePicker = ({ label, errorDate, errorHour, errorDateMessage, errorHourMessage, selected, lastDay, dateFormat, hourFormat, labelEnd, style, onChangeDate, ...rest }: DatePickerProps) => {
  const [startDate, setStartDate] = useState(new Date(selected ?? ''));
  const [endDate, setEndDate] = useState(lastDay ? new Date(lastDay) : null);

  const filterPassedTime = (time: any) => {
    const currentDate = new Date();
    const selectedDate = new Date(time);

    return currentDate.getTime() < selectedDate.getTime();
  };

  const addDaysToDate = (date: any, days: any) => {
    var res = new Date(date);
    res.setDate(res.getDate() + days);
    return res;
  }

  const CustomInputDate = forwardRef(({ value, onClick, onChange }: any, ref) => (
    <Input
      value={value}
      error={errorDate}
      errorMessage={errorDateMessage}
      onClick={onClick}
      onChange={onChange}
      style={{ width: "19.2rem" }}
      className="sb-datePicker__inputDate"
      iconInput="calendar" 
      label={!!label ? label : ""}    />
  ));

  const CustomInputHour = forwardRef(({ value, onClick, onChange }: any, ref) => (
    <Input
      value={value}
      error={errorHour}
      errorMessage={errorHourMessage}
      onClick={onClick}
      onChange={onChange}
      style={{ width: "11.2rem" }}
      className="sb-datePicker__inputHour"
      iconInput="chevron-down" 
      label={!!label ? label : ""}    />
  ));

  const endPropsStart = endDate ? {
    selectsStart: true,
    startDate: startDate,
    endDate: endDate
  } : {};

  const endPropsEnd = endDate ? {
    selectsStart: true,
    startDate: startDate,
    endDate: endDate,
    minDate: startDate
  } : {};

  useEffect(() => {
    if (endDate && (startDate.getTime() >= endDate.getTime())) {
      setEndDate(addDaysToDate(startDate, 2))
    }

  }, [startDate, endDate]);

  return (
    <div
      className="sb-datePicker"
      data-testid="sb-datePicker"
      style={{ ...style }}
    >
      <div
        className="sb-datePicker__container--calendar"
        data-testid="sb-datePicker__container--calendar"
      >
        {label &&
          <div
            className="sb-datePicker__label flex mb-xsm"
            data-testid="sb-datePicker__label"
          >
            <span>{label}</span>
          </div>
        }
        <div
          className="sb-datePicker__container"
          data-testid="sb-datePicker__container"
        >
          <div
            className="sb-datePicker__date"
            data-testid="sb-datePicker__date"
          >
            <ReactDatePicker
              name="start_date"
              showPopperArrow={false}
              locale={es}
              selected={startDate}
              onChange={(date: any) => {
                setStartDate(date);
                !!onChangeDate && onChangeDate(date);
              }}
              dateFormat={dateFormat}
              {...rest}
              {...endPropsStart}
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }: any) => (
                <div className="flex justify-between p-lg items-center text-neutral-04 bg-primary-80">
                  <button
                    className={`flex focus:outline-none ${prevMonthButtonDisabled ? "opacity-50" : ""
                      }`}
                    type="button"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                  >
                    <Icon size={18} iconName="chevron-left" />
                  </button>
                  <div className="font-black text-small-01">
                    {`${months[getMonth(date)]} ${getYear(date)}`}
                  </div>
                  <button
                    className={`flex focus:outline-none ${nextMonthButtonDisabled ? "opacity-50" : ""
                      }`}
                    type="button"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                  >
                    <Icon size={18} iconName="chevron-right" />
                  </button>
                </div>
              )}
              customInput={<CustomInputDate />}
            />
          </div>
          <div
            className="sb-datePicker__hour"
            data-testid="sb-datePicker__hour"
          >
            <ReactDatePicker
              name="start_hour"
              selected={startDate}
              onChange={(date: any) => {
                setStartDate(date);
                !!onChangeDate && onChangeDate(date);
              }}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={60}
              filterTime={filterPassedTime}
              dateFormat={hourFormat}
              customInput={<CustomInputHour />}
            />
          </div>
        </div>
      </div>
      {endDate &&
        <>
          <Icon
            iconName="arrow-right"
            size={20}
            style={{ color: '#7A959F', marginTop: "2.2rem" }}
            className="mx-lg"
          />
          <div className="sb-datePicker__container--calendar">
            {labelEnd &&
              <div
                className="sb-datePicker__label flex mb-xsm"
                data-testid="sb-datePicker__label--end"
              >
                <span>{labelEnd}</span>
              </div>
            }
            <div
              className="sb-datePicker__container"
              data-testid="sb-datePicker__container--end"
            >
              <div className="sb-datePicker__date">
                <ReactDatePicker
                  showPopperArrow={false}
                  name="end_date"
                  locale={es}
                  selected={endDate}
                  onChange={(date: any) => {
                    setEndDate(date);
                    !!onChangeDate && onChangeDate(date);
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  dateFormat={dateFormat}
                  {...rest}
                  {...endPropsEnd}
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }: any) => (
                    <div className="flex justify-between p-lg items-center text-neutral-04 bg-primary-80">
                      <button
                        className={`flex focus:outline-none ${prevMonthButtonDisabled ? "opacity-50" : ""
                          }`}
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                      >
                        <Icon size={18} iconName="chevron-left" />
                      </button>
                      <div className="font-black text-small-01">
                        {`${months[getMonth(date)]} ${getYear(date)}`}
                      </div>
                      <button
                        className={`flex focus:outline-none ${nextMonthButtonDisabled ? "opacity-50" : ""
                          }`}
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                      >
                        <Icon size={18} iconName="chevron-right" />
                      </button>
                    </div>
                  )}
                  customInput={<CustomInputDate />}
                />
              </div>
              <div className="sb-datePicker__hour">
                <ReactDatePicker
                  selected={endDate}
                  name="end_hour"
                  onChange={(date: any) => {
                    setEndDate(date);
                    !!onChangeDate && onChangeDate(date);
                  }}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={60}
                  filterTime={filterPassedTime}
                  dateFormat={hourFormat}
                  customInput={<CustomInputHour />}
                />
              </div>
            </div>
          </div>
        </>
      }
    </div>
  );
};

DatePicker.propTypes = {
  /**
* style
*/
  style: PropTypes.object,
  /**
* label
*/
  label: PropTypes.string,
  /**
* labelEnd
*/
  labelEnd: PropTypes.string,
  /**
* selected
*/
  selected: PropTypes.instanceOf(Date),
  /**
* is errorDate?
*/
  errorDate: PropTypes.bool,
  /**
* errorDateMessage
*/
  errorDateMessage: PropTypes.string,
  /**
* is errorHour?
*/
  errorHour: PropTypes.bool,
  /**
  * errorHourMessage
  */
  errorHourMessage: PropTypes.string,
  /**
* dateFormat
*/
  dateFormat: PropTypes.string,
  /**
* hourFormat
*/
  hourFormat: PropTypes.string,
  /**
   * onChangeDate
   */
   onChangeDate: PropTypes.func,
};

DatePicker.defaultProps = {
  style: {},
  label: "",
  labelEnd: "",
  errorDate: false,
  errorHour: false,
  errorDateMessage: "",
  errorHourMessage: "",
  minDate: new Date(),
  selected: new Date(),
  lastDay: null,
  dateFormat: "dd/MM/yyyy",
  hourFormat: "hh:mm",
  onChangeDate: () => { },
};

export default DatePicker;